import React, { useMemo } from 'react';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

function initials(nome) {
  const parts = String(nome || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

/**
 * Posiciona fiscais em 1 ou 2 anéis para reduzir sobreposição.
 */
function layoutNodes(fiscais) {
  const n = fiscais.length;
  const withAdd = n + 1;
  const useDual = withAdd > 9;
  const nodes = [];

  if (!useDual) {
    const radiusPct = withAdd <= 6 ? 36 : 40;
    for (let i = 0; i < n; i += 1) {
      const angle = (2 * Math.PI * i) / withAdd - Math.PI / 2;
      nodes.push({
        ...fiscais[i],
        x: 50 + radiusPct * Math.cos(angle),
        y: 50 + radiusPct * Math.sin(angle),
        ring: 1,
      });
    }
    const addAngle = (2 * Math.PI * n) / withAdd - Math.PI / 2;
    return {
      nodes,
      add: {
        x: 50 + radiusPct * Math.cos(addAngle),
        y: 50 + radiusPct * Math.sin(addAngle),
      },
      compact: withAdd > 7,
      dual: false,
    };
  }

  // Anel interno + externo
  const innerCount = Math.ceil(n / 2);
  const outerItems = n - innerCount;
  const outerSlots = outerItems + 1; // + novo fiscal
  const rInner = 28;
  const rOuter = 44;

  for (let i = 0; i < innerCount; i += 1) {
    const angle = (2 * Math.PI * i) / innerCount - Math.PI / 2;
    nodes.push({
      ...fiscais[i],
      x: 50 + rInner * Math.cos(angle),
      y: 50 + rInner * Math.sin(angle),
      ring: 0,
    });
  }
  for (let i = 0; i < outerItems; i += 1) {
    const angle = (2 * Math.PI * i) / outerSlots - Math.PI / 2;
    nodes.push({
      ...fiscais[innerCount + i],
      x: 50 + rOuter * Math.cos(angle),
      y: 50 + rOuter * Math.sin(angle),
      ring: 1,
    });
  }
  const addAngle = (2 * Math.PI * outerItems) / outerSlots - Math.PI / 2;
  return {
    nodes,
    add: {
      x: 50 + rOuter * Math.cos(addAngle),
      y: 50 + rOuter * Math.sin(addAngle),
    },
    compact: true,
    dual: true,
  };
}

/**
 * Mapa de fiscais (radar) — fluxo do hub /home.
 */
export default function FiscalMapView({
  fiscais = [],
  loading,
  error,
  onBack,
  onOpenContratos,
  onOpenFiscal,
  onNovoFiscal,
  onRetry,
}) {
  const layout = useMemo(() => layoutNodes(fiscais), [fiscais]);

  const totalContratos = fiscais.reduce((s, f) => s + (f.qtdContratos || 0), 0);
  const alertas = fiscais.filter((f) => (f.qtdContratos || 0) === 0).length;

  return (
    <div className="fiscal-map fiscal-map--enter">
      <div className="fiscal-map__top">
        <p className="fiscal-map__crumb">
          Módulos / <strong>Mapa de fiscais</strong>
        </p>
        <button type="button" className="fiscal-map__back" onClick={onBack}>
          Voltar aos módulos
        </button>
      </div>

      <div className="fiscal-map__kpis">
        <div className="fiscal-map__kpi" style={{ '--i': 0 }}>
          <span className="fiscal-map__kpi-label">Fiscais</span>
          <span className="fiscal-map__kpi-value">{loading ? '…' : fiscais.length}</span>
        </div>
        <div className="fiscal-map__kpi" style={{ '--i': 1 }}>
          <span className="fiscal-map__kpi-label">Contratos</span>
          <span className="fiscal-map__kpi-value">{loading ? '…' : totalContratos}</span>
        </div>
        <div className="fiscal-map__kpi" style={{ '--i': 2 }}>
          <span className="fiscal-map__kpi-label">Sem contrato</span>
          <span className="fiscal-map__kpi-value">{loading ? '…' : alertas}</span>
        </div>
      </div>

      {error && (
        <div className="entry-panel__state" style={{ marginBottom: '1rem' }}>
          <p className="entry-panel__state-title">Erro ao carregar mapa</p>
          <p className="entry-panel__state-message">{error}</p>
          <button type="button" className="entry-panel__state-button" onClick={onRetry}>
            Tentar novamente
          </button>
        </div>
      )}

      {!error && (
        <>
          <div
            className={`fiscal-map__stage${layout.dual ? ' fiscal-map__stage--dual' : ''}${
              layout.compact ? ' fiscal-map__stage--compact' : ''
            }`}
            aria-label="Mapa de fiscais"
          >
            <div className="fiscal-map__ring fiscal-map__ring--spin-slow" style={{ width: '92%', height: '92%' }} />
            <div className="fiscal-map__ring fiscal-map__ring--spin" style={{ width: '68%', height: '68%' }} />
            <div className="fiscal-map__ring fiscal-map__ring--spin-rev" style={{ width: '42%', height: '42%' }} />

            <button
              type="button"
              className="fiscal-map__core fiscal-map__core--pulse"
              onClick={onOpenContratos}
              title="Abrir módulo Contratos consolidado"
              aria-label="Abrir módulo Contratos consolidado"
            >
              <DescriptionIcon sx={{ fontSize: 28 }} />
              <span className="fiscal-map__core-label">Contratos</span>
            </button>

            {loading && (
              <p
                className="fiscal-map__hint"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  placeItems: 'center',
                  zIndex: 6,
                }}
              >
                Carregando fiscais…
              </p>
            )}

            {!loading &&
              layout.nodes.map((f, idx) => (
                <div
                  key={f.id}
                  className="fiscal-map__node fiscal-map__node--pop"
                  style={{
                    left: `${f.x}%`,
                    top: `${f.y}%`,
                    '--i': idx,
                  }}
                >
                  <button
                    type="button"
                    className="fiscal-map__node-btn"
                    onClick={() => onOpenFiscal?.(f)}
                    title={`${f.nome} — ${f.setor || 'sem setor'}`}
                  >
                    {(f.qtdContratos || 0) === 0 && (
                      <span className="fiscal-map__badge" title="Fiscal sem contrato">
                        !
                      </span>
                    )}
                    <div className="fiscal-map__node-avatar">{initials(f.nome)}</div>
                    <p className="fiscal-map__node-setor">{f.setor || 'Sem setor'}</p>
                    <p className="fiscal-map__node-nome">{f.nome}</p>
                    <p className="fiscal-map__node-qtd">
                      {f.qtdContratos || 0} contrato{(f.qtdContratos || 0) === 1 ? '' : 's'}
                    </p>
                  </button>
                </div>
              ))}

            {!loading && (
              <div
                className="fiscal-map__node fiscal-map__node--pop"
                style={{
                  left: `${layout.add.x}%`,
                  top: `${layout.add.y}%`,
                  '--i': layout.nodes.length,
                }}
              >
                <button
                  type="button"
                  className="fiscal-map__node-btn fiscal-map__node-btn--add"
                  onClick={onNovoFiscal}
                  title="Cadastrar novo fiscal"
                  aria-label="Cadastrar novo fiscal"
                >
                  <div className="fiscal-map__node-avatar">
                    <PersonAddIcon sx={{ fontSize: 18 }} />
                  </div>
                  <p className="fiscal-map__node-setor">Cadastro</p>
                  <p className="fiscal-map__node-nome">Novo fiscal</p>
                  <p className="fiscal-map__node-qtd">Abrir tela existente</p>
                </button>
              </div>
            )}
          </div>
          <p className="fiscal-map__hint">
            Núcleo = módulo Contratos consolidado · bolinha = módulo isolado do fiscal
          </p>
        </>
      )}
    </div>
  );
}
